import isNumber from 'lodash/isNumber'

import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useDepositSubmit } from './useDepositSubmit'
import { useDepositRiskOptions } from 'modules/pools/hooks/useDepositRiskOptions'
import { useAssetBalance } from 'modules/pools/hooks/useAssetBalance'
import { useQueryParams } from 'modules/router/hooks/useQueryParams'
import { useTargetRiskPoolModal } from '../TargetRiskPoolModal/useTargetRiskPoolModal'
import { useDepositLimit } from './useDepositLimit'
import { usePoolProjectedApy } from 'modules/pools/hooks/usePoolProjectedApy'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

import { Text } from 'shared/ui/common/Text'
import { InputControl } from 'shared/ui/controls/Input'
import { SelectControl } from 'shared/ui/controls/Select'
import { FormSubmitter } from 'shared/ui/common/FormSubmitter'
import { Form } from 'shared/ui/controls/FormNetworkGuarded'
import {
  FormInfoItem,
  FormInfoFrame,
  FormInfoFramesList,
} from 'shared/ui/common/FormInfoFrame'
import {
  FormLockedValue,
  FormLockedValuesList,
} from 'shared/ui/common/FormLockedValue'
import { FormTransactionRow } from 'modules/blockChain/ui/FormTransactionRow'
import { InputMaxAction } from 'shared/ui/controls/InputMaxAction'
import { EstimatedGasFee } from 'modules/blockChain/ui/EstimatedGasFee'
import { DoubleBubbleCTA } from 'shared/ui/controls/DoubleBubbleCTA'
import { ReactComponent as QuestionMarkSvg } from 'assets/question-mark.svg'

import * as formErrors from 'shared/constants/formErrors'
import {
  poolAssetOptions,
  getPoolAssetIcon,
} from 'modules/pools/constants/poolAssets'
import { PoolRisk } from 'modules/pools/constants/PoolRisk'
import { assetOrUndef } from 'modules/pools/utils/assetOrUndef'
import { weiToNum } from 'modules/blockChain/utils/parseWei'
import type { FormValues, SuccessData } from './types'
import { formatAsset } from 'modules/pools/utils/formatAsset'
import { getLinkBridge } from 'modules/pools/utils/getLinkBrige'
import { getLinkOneInch } from 'modules/pools/utils/getLinkOneInch'
import { useFirebase } from 'modules/analytics/firebase'
import {
  DEPOSIT_LIMIT_ERROR_EVENT,
  DEPOSIT_POOL_SELECT_EVENT,
} from 'modules/analytics/events'
import { FormDepositWarning } from '../FormDepositWarning'
import { WarningCard } from 'shared/ui/common/WarningCard'

import s from './FormDeposit.module.scss'

const depositOptions = [poolAssetOptions.USDC]

type Props = {
  onSuccess: (successData: SuccessData) => void
}

export function FormDeposit({ onSuccess }: Props) {
  const { risk } = useQueryParams()
  const { chainId } = useWeb3()

  const formMethods = useForm<FormValues>({
    shouldUnregister: false,
    defaultValues: {
      depositedAsset: depositOptions[0].value,
      amount: '',
      targetRiskPool: assetOrUndef(PoolRisk, risk as string),
    },
  })

  const targetRiskPoolModal = useTargetRiskPoolModal()

  const { watch, trigger, setValue, formState } = formMethods

  const amount = watch('amount')
  const depositedAsset = watch('depositedAsset')
  const targetRiskPool = watch('targetRiskPool')

  const { limitError, isLoading, maxByLimit } = useDepositLimit({
    amount,
    depositedAsset,
    risk: targetRiskPool,
  })

  const riskOptions = useDepositRiskOptions(depositedAsset)
  const { data: depositedAssetBalanceWei } = useAssetBalance(depositedAsset)
  const { getAssetDecimals } = useAssetsData()
  const { trackEvent } = useFirebase()
  const { data: apy } = usePoolProjectedApy({
    risk: targetRiskPool,
    asset: depositedAsset,
  })

  const earning = apy ? (Number(amount) * apy * 30) / 365 / 100 : 0

  const {
    submit,
    isSubmitting,
    txAllowance,
    isLocked,
    unlockForm,
    estimatedGas,
  } = useDepositSubmit({
    formMethods,
    onSuccess,
  })

  const depositedAssetDecimals = getAssetDecimals(depositedAsset)

  const handleClickMaxAmount = useCallback(() => {
    if (!depositedAsset) {
      trigger('depositedAsset')
      return
    }
    if (!depositedAssetBalanceWei) return
    const maxByBalance = weiToNum(
      depositedAssetBalanceWei,
      depositedAssetDecimals,
    )
    const maxAmount = Math.min(
      maxByBalance,
      isNumber(maxByLimit) ? maxByLimit : Infinity,
    )
    setValue('amount', maxAmount.toFixed(depositedAssetDecimals))
  }, [
    depositedAsset,
    depositedAssetBalanceWei,
    depositedAssetDecimals,
    maxByLimit,
    setValue,
    trigger,
  ])

  /*
    This useEffect triggers every time
    some of the deps of amount.validate func changes.
    That way we handle validation not only on value change, but also
    on every one of the deps change.
  */
  useEffect(() => {
    if (amount.length) {
      setTimeout(() => trigger('amount'), 10)
    }
  }, [depositedAssetBalanceWei, depositedAsset, limitError, trigger, amount])

  useEffect(() => {
    if (targetRiskPool) {
      trackEvent(DEPOSIT_POOL_SELECT_EVENT, { risk: targetRiskPool })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRiskPool])

  const balanceErr = useMemo(() => {
    return formErrors.balance(
      amount,
      depositedAssetBalanceWei,
      depositedAsset,
      depositedAssetDecimals,
    )
  }, [amount, depositedAsset, depositedAssetBalanceWei, depositedAssetDecimals])

  const bridgeLink = getLinkBridge(chainId)
  const oneInchLink = depositedAsset && getLinkOneInch(chainId, depositedAsset)

  useEffect(() => {
    if (!formState.isValid && limitError) {
      trackEvent(DEPOSIT_LIMIT_ERROR_EVENT, {
        amount: amount.toString(),
        asset: depositedAsset,
      })
    }
    // We need this hook to trigger each time 'submit' button hits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.submitCount])

  return (
    <Form formMethods={formMethods} onSubmit={submit}>
      <WarningCard
        title={
          <>
            V1 will be shut down soon. Please use{' '}
            <a
              href="https://roci.fi/app"
              target="_blank"
              className={s.v2Link}
              rel="noreferrer"
            >
              RociFi V2
            </a>
            .
          </>
        }
      />
      {!isLocked && (
        <>
          <SelectControl
            name="depositedAsset"
            placeholder="Deposited asset"
            options={depositOptions}
            rules={{ required: 'Please select the asset to deposit' }}
            readOnly
          />
          <InputControl
            name="amount"
            concat="bottom"
            placeholder="Amount"
            onlyNumber
            readOnly
            rules={{
              required: formErrors.required,
              validate: val =>
                formErrors.notLess(val, 0.01) ||
                limitError ||
                balanceErr ||
                true,
            }}
            testId="deposit-form-amount-input"
            action={
              targetRiskPool && (
                <InputMaxAction
                  onClick={handleClickMaxAmount}
                  testId="deposit-form-max-amount-button"
                />
              )
            }
          />
          <SelectControl
            className={s.selectBeforeHint}
            name="targetRiskPool"
            concat="top"
            placeholder="Target risk pool"
            rules={{ required: formErrors.required }}
            options={riskOptions}
            readOnly
            testId="deposit-form-risk-pool-select"
          />
          <Text className={s.hint} size={14} weight={500} color="secondary">
            About RociFi pools
            <div
              className={s.icon}
              onClick={() => targetRiskPoolModal.open({})}
            >
              <QuestionMarkSvg />
            </div>
          </Text>

          {typeof balanceErr === 'string' &&
            depositedAsset &&
            bridgeLink &&
            oneInchLink && (
              <DoubleBubbleCTA
                asset={depositedAsset}
                bridgeLink={bridgeLink}
                oneInchLink={oneInchLink}
              />
            )}
        </>
      )}

      {isLocked && (
        <FormLockedValuesList>
          <FormLockedValue
            label="Deposited asset"
            name="depositedAsset"
            getIcon={getPoolAssetIcon}
          />
          <FormLockedValue
            label="Amount"
            name="amount"
            showTooltipUnformatted
            formatValue={v => formatAsset(v, depositedAsset)}
          />
          <FormLockedValue
            label="Target risk pool"
            name="targetRiskPool"
            valueSize={16}
            formatValue={v => riskOptions.find(i => i.value === v)?.label}
          />
        </FormLockedValuesList>
      )}

      <FormInfoFramesList>
        <FormInfoFrame>
          <FormInfoItem
            label={
              <>
                Annual
                <br />
                percentage yield
              </>
            }
            value={apy ? `${apy.toFixed(2)}%` : <>&nbsp;</>}
          />
          <FormInfoItem
            label={
              <>
                Estimated
                <br />
                earned per 30 days
              </>
            }
            sign={depositedAsset}
            value={formatAsset(earning, depositedAsset)}
            tooltip={earning}
          />
        </FormInfoFrame>
      </FormInfoFramesList>

      <FormDepositWarning />

      {isLocked && (
        <div>
          <FormTransactionRow
            label="Token spending approval"
            tx={txAllowance}
          />
        </div>
      )}

      {isLocked && estimatedGas && <EstimatedGasFee value={estimatedGas} />}

      <FormSubmitter
        isLocked={isLocked}
        isSubmitting={isSubmitting || isLoading}
        isDisabled
        firstStepText="Deposit"
        submitTestId="deposit-form-submit-button"
        confirmTestId="deposit-form-confirm-button"
        onClickUnlock={unlockForm}
      />
    </Form>
  )
}
